import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Checkbox,
  Stack,
  Icon,
  useColorModeValue,
  useColorMode,
  useToast,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  IconButton,
  useDisclosure,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  InputGroup,
  InputRightElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
  Center,
} from '@chakra-ui/react';
import {
  FiSettings,
  FiUser,
  FiDroplet,
  FiGlobe,
  FiBell,
  FiShield,
  FiCopy,
  FiCheck,
  FiLogOut,
  FiArrowLeft,
  FiRefreshCw,
  FiTrash2,
} from 'react-icons/fi';
import { useAccount, useDisconnect, useNetwork } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';
import PageHeader from '@/components/common/PageHeader';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';

import { useSettings } from '@/hooks/useSettings';
import i18nInstance from '@/i18n';

// 通用设置组件
const GeneralSettings: React.FC = () => {
  const { t } = useTranslation('settings');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const { 
    settings, 
    isLoading, 
    updateSetting, 
    resetSettings, 
    storageAvailable,
    hasUnsavedChanges
  } = useSettings();

  // 重置设置处理
  const handleResetSettings = () => {
    resetSettings();
    onClose();
  };

  if (isLoading) {
    return (
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <Center py={8}>
            <Spinner size="lg" color="blue.500" />
          </Center>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg={cardBg} border="1px" borderColor={borderColor}>
      <CardHeader>
        <HStack justify="space-between">
          <HStack>
            <FiSettings color="blue.500" />
            <Heading size="md">{t('generalSettings')}</Heading>
          </HStack>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            leftIcon={<FiTrash2 />}
            onClick={onOpen}
          >
            {t('resetSettings')}
          </Button>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* 主题设置 */}
          <FormControl>
            <FormLabel>
              <HStack>
                <FiDroplet />
                <Text>{t('theme')}</Text>
              </HStack>
            </FormLabel>
            <Select 
              value={settings.theme} 
              onChange={(e) => updateSetting('theme', e.target.value as 'light' | 'dark' | 'system')}
            >
              <option value="light">{t('light')}</option>
              <option value="dark">{t('dark')}</option>
              <option value="system">{t('auto')}</option>
            </Select>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('themeDescription')}
            </Text>
          </FormControl>

          {/* 语言设置 */}
          <FormControl>
            <FormLabel>
              <HStack>
                <FiGlobe />
                <Text>{t('language')}</Text>
              </HStack>
            </FormLabel>
            <LanguageSwitcher />
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('languageDescription')}
            </Text>
          </FormControl>

          {/* 通知设置 */}
          <FormControl>
            <FormLabel>
              <HStack>
                <FiBell />
                <Text>{t('enableNotifications')}</Text>
              </HStack>
            </FormLabel>
            <Switch 
              isChecked={settings.notifications} 
              onChange={(e) => updateSetting('notifications', e.target.checked)}
              colorScheme="blue"
            />
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('notificationsDescription')}
            </Text>
            
            {settings.notifications && (
              <Stack mt={4} pl={6} spacing={3}>
                <Checkbox 
                  isChecked={settings.priceAlerts}
                  onChange={(e) => updateSetting('priceAlerts', e.target.checked)}
                >
                  {t('priceAlerts')}
                </Checkbox>
                <Checkbox 
                  isChecked={settings.inviteUpdates}
                  onChange={(e) => updateSetting('inviteUpdates', e.target.checked)}
                >
                  {t('inviteUpdates')}
                </Checkbox>
              </Stack>
            )}


          </FormControl>

          {/* 预览区域 */}
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={2}>{t('preview')}</Text>
            <Card variant="outline" size="sm">
              <CardBody>
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm">{t('currentTheme')}: {settings.theme === 'system' ? t('auto') : settings.theme === 'light' ? t('light') : t('dark')}</Text>
                  <Text fontSize="sm">{t('notifications')}: {settings.notifications ? t('enabled') : t('disabled')}</Text>
                  <Text fontSize="sm">{t('priceAlerts')}: {settings.priceAlerts ? t('enabled') : t('disabled')}</Text>
                  <Text fontSize="sm">{t('inviteUpdates')}: {settings.inviteUpdates ? t('enabled') : t('disabled')}</Text>
                  {hasUnsavedChanges && (
                    <Text fontSize="sm" color="orange.500" fontWeight="medium">
                      ⚠️ {t('unsavedChangesWarning')}
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </Box>

          {/* 存储状态 */}
          <Alert status={storageAvailable ? "success" : "warning"} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('storageStatus')}</AlertTitle>
              <AlertDescription>
                {storageAvailable 
                  ? t('storageAvailable')
                  : t('storageUnavailable')
                }
              </AlertDescription>
            </Box>
          </Alert>
        </VStack>
      </CardBody>

      {/* 重置确认对话框 */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('resetSettingsTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {t('resetSettingsDescription')}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button colorScheme="red" onClick={handleResetSettings}>
              {t('resetAllSettings')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

// 账户信息组件
const AccountInfo: React.FC = () => {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { chain } = useNetwork();
  const toast = useToast();
  const { t } = useTranslation('settings');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [copied, setCopied] = useState(false);

  // 复制地址
  const copyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      toast({
        title: t('success.copied'),
        description: t('addressCopied'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 断开连接
  const handleDisconnect = () => {
    disconnect();
    toast({
      title: t('success.disconnected'),
      description: t('walletDisconnected'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // 格式化地址
  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // 获取网络状态
  const getNetworkStatus = () => {
    if (!chain) return t('unknown');
    return chain.name;
  };

  // 模拟DOR持有量数据
  const mockDORHoldings = {
    circulating: 150.00,
    locked: 200.00,
    total: 350.00,
  };

  return (
    <Card bg={cardBg} border="1px" borderColor={borderColor}>
      <CardHeader>
        <HStack>
          <FiUser color="blue.500" />
          <Heading size="md">{t('accountInfo')}</Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* 钱包地址 */}
          <FormControl>
            <FormLabel>{t('walletAddress')}</FormLabel>
            <InputGroup>
              <Input
                value={address ? formatAddress(address) : t('notConnected')}
                isReadOnly
                fontFamily="mono"
              />
              <InputRightElement width="4.5rem">
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={copied ? <FiCheck /> : <FiCopy />}
                  onClick={copyAddress}
                  aria-label={t('copyAddress')}
                  isDisabled={!address}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          {/* 网络状态 */}
          <FormControl>
            <FormLabel>{t('network')}</FormLabel>
            <HStack>
              <Badge 
                colorScheme={isConnected ? 'green' : 'red'}
                size="md"
              >
                {isConnected ? t('connected') : t('disconnected')}
              </Badge>
              {chain && (
                <Text fontSize="sm" color="gray.500">
                  {getNetworkStatus()}
                </Text>
              )}
            </HStack>
          </FormControl>

          {/* DOR持有量 */}
          <FormControl>
            <FormLabel>{t('dorHoldings')}</FormLabel>
            <VStack spacing={3} align="stretch">
              <Stat>
                <StatLabel>{t('circulatingDOR')}</StatLabel>
                <StatNumber>{mockDORHoldings.circulating.toFixed(2)} DOR</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>{t('lockedDOR')}</StatLabel>
                <StatNumber>{mockDORHoldings.locked.toFixed(2)} DOR</StatNumber>
              </Stat>
              <Divider />
              <Stat>
                <StatLabel>{t('totalDOR')}</StatLabel>
                <StatNumber color="blue.500">{mockDORHoldings.total.toFixed(2)} DOR</StatNumber>
              </Stat>
            </VStack>
          </FormControl>

          {/* 断开连接按钮 */}
          <Button
            colorScheme="red"
            variant="outline"
            leftIcon={<FiLogOut />}
            onClick={handleDisconnect}
            isDisabled={!isConnected}
          >
            {t('disconnectWallet')}
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

// 主设置组件
const Settings: React.FC = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  const { saveAllSettings, hasUnsavedChanges } = useSettings();

  // 保存设置处理
  const handleSaveSettings = () => {
    const success = saveAllSettings();
    if (success) {
      // 可以在这里添加其他保存后的逻辑
      // Settings saved successfully
    }
  };

  // 未连接钱包时的提示
  if (!isConnected) {
    return (
      <Box>
        <VStack spacing={6} align="stretch">
          {/* 顶部区域 */}
          <PageHeader title={t('title')} />
          
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody textAlign="center" py={12}>
                              <Box mb={4}>
                  <FiSettings size={64} color="var(--chakra-colors-blue-500)" />
                </Box>
              <Heading size="md" mb={2}>
                {t('connectWalletPrompt')}
              </Heading>
              <Text color="gray.500" mb={6}>
                {t('connectWalletDescription')}
              </Text>
              <ConnectButton />
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* 顶部区域 */}
        <PageHeader 
          title={t('title')} 
          subtitle={t('subtitle')}
        />

        {/* 主要内容 */}
        <Tabs variant="enclosed">
          <TabList>
            <Tab>{t('generalSettings')}</Tab>
            <Tab>{t('accountInfo')}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <GeneralSettings />
            </TabPanel>
            <TabPanel>
              <AccountInfo />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* 底部行动区 */}
        <VStack spacing={4}>
          <Button
            colorScheme="blue"
            size="lg"
            w="full"
            leftIcon={<FiShield />}
            onClick={handleSaveSettings}
            isDisabled={!hasUnsavedChanges}
            opacity={hasUnsavedChanges ? 1 : 0.6}
          >
            {hasUnsavedChanges ? t('saveChanges') : t('allSettingsSaved')}
          </Button>

          {hasUnsavedChanges && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                {t('unsavedChangesWarning')}
              </AlertDescription>
            </Alert>
          )}

          {/* 隐私声明 */}
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('privacyStatement')}</AlertTitle>
              <AlertDescription>
                {t('privacyDescription')}
              </AlertDescription>
            </Box>
          </Alert>
        </VStack>
      </VStack>
    </Box>
  );
};

export default Settings; 